import { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Header
    nav: {
      services: 'Services',
      gallery: 'Gallery',
      about: 'About',
      contact: 'Contact',
      bookNow: 'Book Now',
    },
    // Hero Section
    hero: {
      badge: 'For Kids 6 months - 14 years',
      title1: 'Play. Learn.',
      titleHighlight: 'Celebrate.',
      title2: 'Repeat. 🐻🍭',
      description: 'Welcome to Mascot&Candy a place where children discover the Joy of Play, meet their Favourite Mascots, Celebrate Unforgettable Birthdays, enjoy Sweet Candy, and Learn.',
      bookBirthday: 'Book Birthday',
      exploreServices: 'Explore Services',
      birthdayParties: 'Birthday Parties',
      mascots: 'Mascots',
      candyBar: 'Candy Bar',
      stemEducation: 'STEM Education',
      scrollToExplore: 'Scroll to explore',
    },
    // Services Section
    services: {
      badge: 'Our Services',
      title: 'What We Offer',
      subtitle: 'Create unforgettable memories with our range of services designed to bring joy to every child',
      birthday: {
        title: 'Birthday Packages',
        description: 'Celebrate your child\'s special day with our amazing birthday packages. From decorations to entertainment, we handle everything!',
        features: ['Themed decorations', 'Dedicated party host', 'Birthday cake included'],
      },
      mascot: {
        title: 'Mascot Reservation',
        description: 'Book your favorite character mascots for an unforgettable experience. Mickey, Minnie, Spiderman, and more!',
        features: ['10 characters available', 'Photo sessions', 'Interactive games', 'Custom appearances'],
      },
      candy: {
        title: 'Candy Bar Service',
        description: 'Add sweetness to any event with our delightful candy bar service. Custom setups for parties and events.',
        features: ['Classic to deluxe options', 'Custom candy selection', 'Beautiful display setup', 'Take-home bags'],
      },
      stem: {
        title: 'STEM Education',
        description: 'An educational space combining Science, Technology, Engineering & Maths together — because in real life, these subjects work together!',
        features: ['Ages 4-14 years', 'Hands-on learning', 'Free with entry fee', 'Fun & educational'],
      },
      bookNow: 'Book Now',
      registerNow: 'Register Now',
    },
    // Gallery Section
    gallery: {
      badge: 'Gallery',
      title: 'Explore Our Space',
      subtitle: 'Take a peek at our colorful indoor and outdoor playgrounds, birthday celebrations, and special events',
      filters: {
        all: 'All',
        indoor: 'Indoor',
        outdoor: 'Outdoor',
        birthday: 'Birthday',
        mascots: 'Mascots',
        candy: 'Candy Bar',
        stem: 'STEM Education',
      },
    },
    // About Section
    about: {
      badge: 'About Us',
      title1: 'Where Childhood',
      titleHighlight: 'Dreams',
      title2: 'Come True',
      description: 'Mascot&Candy is more than just a Playground – it\'s a magical space where children can explore, play, learn in our supervised digital learning station to sparks the next generation of curious minds and create lasting memories. Our state-of-the-art facilities cater to kids from 6 months to 14 years old, offering a safe and exciting environment for endless fun.',
      features: {
        safe: { title: 'Safe Environment', description: 'All play areas are designed with safety as our top priority, with soft padding and supervised zones.' },
        love: { title: 'Made with Love', description: 'Every event is crafted with care to ensure your child has the best experience possible.' },
        quality: { title: 'Quality Service', description: 'Our trained staff ensures top-notch service and attention to detail for every celebration.' },
        flexible: { title: 'Flexible Hours', description: 'Open 7 days a week with flexible booking options to suit your schedule.' },
      },
      location: 'Location',
      callUs: 'Call Us',
      happyKids: 'Happy Kids',
    },
    // Footer
    footer: {
      slogan: 'Play. Learn. Celebrate. Repeat. 🐻🍭 Indoor and Outdoor playground for kids from 6 months to 14 years old.',
      quickLinks: 'Quick Links',
      ourServices: 'Our Services',
      contactUs: 'Contact Us',
      birthdayPackage: 'Birthday Package',
      mascotReservation: 'Mascot Reservation',
      candyBar: 'Candy Bar',
      stemEducation: 'STEM Education',
      copyright: '© 2024 Mascot&Candy. All rights reserved.',
      privacyPolicy: 'Privacy Policy',
      termsOfService: 'Terms of Service',
    },
    // Birthday Booking Page
    birthdayBooking: {
      backToHome: 'Back to Home',
      title: 'Book a Birthday Party',
      subtitle: 'Create magical memories for your child with our birthday packages',
      choosePackage: 'Choose Your Package',
      yourDetails: 'Your Details',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Enter your full name',
      mobilePhone: 'Mobile Phone',
      partyDate: 'Party Date',
      selectDate: 'Select a date',
      numberOfGuests: 'Number of Guests',
      bookBirthdayParty: 'Book Birthday Party',
      booking: 'Booking...',
      successTitle: 'Birthday party booked successfully!',
      successDescription: 'We will contact you shortly to confirm your booking.',
      errorTitle: 'Booking failed',
      errorDescription: 'Please try again later',
      selectDateAndPackage: 'Please select a date and package',
    },
    // Mascot Booking Page
    mascotBooking: {
      backToHome: 'Back to Home',
      title: 'Reserve a Mascot',
      subtitle: 'Bring your child\'s favorite character to life at your event',
      chooseMascot: 'Choose Your Mascot',
      yourDetails: 'Your Details',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Enter your full name',
      mobilePhone: 'Mobile Phone',
      eventDate: 'Event Date',
      selectDate: 'Select a date',
      eventDetails: 'Event Details (Optional)',
      eventDetailsPlaceholder: 'Tell us about your event (location, time, special requests...)',
      reserveMascot: 'Reserve Mascot',
      booking: 'Booking...',
      successTitle: 'Mascot reservation successful!',
      successDescription: 'We will contact you shortly to confirm your booking.',
      errorTitle: 'Booking failed',
      errorDescription: 'Please try again later',
      selectDateAndMascot: 'Please select a date and mascot',
    },
    // Candy Bar Booking Page
    candyBarBooking: {
      backToHome: 'Back to Home',
      title: 'Book a Candy Bar',
      subtitle: 'Add sweetness to your event with our delightful candy bar service',
      chooseSelection: 'Choose Your Selection',
      yourDetails: 'Your Details',
      fullName: 'Full Name',
      fullNamePlaceholder: 'Enter your full name',
      mobilePhone: 'Mobile Phone',
      eventDate: 'Event Date',
      selectDate: 'Select a date',
      numberOfGuests: 'Number of Guests',
      bookCandyBar: 'Book Candy Bar',
      booking: 'Booking...',
      successTitle: 'Candy bar booking successful!',
      successDescription: 'We will contact you shortly to confirm your booking.',
      errorTitle: 'Booking failed',
      errorDescription: 'Please try again later',
      selectDateAndSelection: 'Please select a date and candy selection',
    },
    // STEM Registration Page
    stemRegistration: {
      backToHome: 'Back to Home',
      title: 'STEM Education Program',
      subtitle: 'Register your child for our exciting STEM program where Science, Technology, Engineering & Maths come together!',
      freeBadge: 'FREE with playground entry fee • Ages 4-14 years',
      whatLearn: 'What Your Child Will Learn',
      subjects: {
        science: { name: 'Science', description: 'Explore the world through experiments' },
        technology: { name: 'Technology', description: 'Learn coding and digital skills' },
        engineering: { name: 'Engineering', description: 'Build and create amazing projects' },
        maths: { name: 'Maths', description: 'Problem-solving and logical thinking' },
      },
      selectAgeGroup: 'Select Age Group',
      ageGroups: {
        '4-6': { label: '4-6 years', description: 'Little Explorers' },
        '7-10': { label: '7-10 years', description: 'Junior Scientists' },
        '11-14': { label: '11-14 years', description: 'Young Innovators' },
      },
      whyChoose: 'Why Choose Our STEM Program?',
      benefits: [
        'Hands-on learning experiences',
        'Qualified and passionate instructors',
        'Small group sizes for personalized attention',
        'Fun and engaging curriculum',
      ],
      registerChild: 'Register Your Child',
      parentName: 'Parent/Guardian Name',
      parentNamePlaceholder: 'Enter your full name',
      childName: 'Child\'s Name',
      childNamePlaceholder: 'Enter child\'s name',
      mobilePhone: 'Mobile Phone',
      emailAddress: 'Email Address',
      emailPlaceholder: 'your@email.com',
      additionalInfo: 'Additional Information (Optional)',
      additionalInfoPlaceholder: 'Any special requirements or questions...',
      registerForStem: 'Register for STEM',
      registering: 'Registering...',
      successTitle: 'STEM registration successful!',
      successDescription: 'We will contact you shortly with more details about our STEM program.',
      errorTitle: 'Registration failed',
      errorDescription: 'Please try again later',
      selectAgeGroupError: 'Please select an age group',
    },
    // Common
    common: {
      hoursOfPlayTime: 'hours of play time',
      hoursOfPlay: 'hours of play',
      snacks: 'snacks',
      fullPartyExperience: 'Full party experience',
    },
  },
  fr: {
    // Header
    nav: {
      services: 'Services',
      gallery: 'Galerie',
      about: 'À propos',
      contact: 'Contact',
      bookNow: 'Réserver',
    },
    // Hero Section
    hero: {
      badge: 'Pour enfants de 6 mois à 14 ans',
      title1: 'Jouer. Apprendre.',
      titleHighlight: 'Célébrer.',
      title2: 'Répéter. 🐻🍭',
      description: 'Bienvenue chez Mascot&Candy, un endroit où les enfants découvrent la Joie de Jouer, rencontrent leurs Mascottes Préférées, Célèbrent des Anniversaires Inoubliables, savourent de Délicieux Bonbons et Apprennent.',
      bookBirthday: 'Réserver Anniversaire',
      exploreServices: 'Explorer les Services',
      birthdayParties: 'Fêtes d\'Anniversaire',
      mascots: 'Mascottes',
      candyBar: 'Bar à Bonbons',
      stemEducation: 'Éducation STEM',
      scrollToExplore: 'Défiler pour explorer',
    },
    // Services Section
    services: {
      badge: 'Nos Services',
      title: 'Ce Que Nous Offrons',
      subtitle: 'Créez des souvenirs inoubliables avec notre gamme de services conçus pour apporter de la joie à chaque enfant',
      birthday: {
        title: 'Forfaits Anniversaire',
        description: 'Célébrez le jour spécial de votre enfant avec nos incroyables forfaits anniversaire. Des décorations au divertissement, nous nous occupons de tout!',
        features: ['Décorations thématiques', 'Animateur dédié', 'Gâteau d\'anniversaire inclus'],
      },
      mascot: {
        title: 'Réservation de Mascotte',
        description: 'Réservez vos personnages mascottes préférés pour une expérience inoubliable. Mickey, Minnie, Spiderman et plus encore!',
        features: ['10 personnages disponibles', 'Séances photo', 'Jeux interactifs', 'Apparitions personnalisées'],
      },
      candy: {
        title: 'Service Bar à Bonbons',
        description: 'Ajoutez de la douceur à tout événement avec notre délicieux service de bar à bonbons. Installations personnalisées pour fêtes et événements.',
        features: ['Options classique à deluxe', 'Sélection de bonbons personnalisée', 'Belle présentation', 'Sachets à emporter'],
      },
      stem: {
        title: 'Éducation STEM',
        description: 'Un espace éducatif combinant Sciences, Technologie, Ingénierie et Mathématiques — car dans la vraie vie, ces matières fonctionnent ensemble!',
        features: ['Âges 4-14 ans', 'Apprentissage pratique', 'Gratuit avec l\'entrée', 'Amusant & éducatif'],
      },
      bookNow: 'Réserver',
      registerNow: 'S\'inscrire',
    },
    // Gallery Section
    gallery: {
      badge: 'Galerie',
      title: 'Explorez Notre Espace',
      subtitle: 'Découvrez nos aires de jeux intérieures et extérieures colorées, les célébrations d\'anniversaire et les événements spéciaux',
      filters: {
        all: 'Tout',
        indoor: 'Intérieur',
        outdoor: 'Extérieur',
        birthday: 'Anniversaire',
        mascots: 'Mascottes',
        candy: 'Bar à Bonbons',
        stem: 'Éducation STEM',
      },
    },
    // About Section
    about: {
      badge: 'À Propos',
      title1: 'Où les Rêves',
      titleHighlight: 'd\'Enfance',
      title2: 'Deviennent Réalité',
      description: 'Mascot&Candy est plus qu\'une simple aire de jeux – c\'est un espace magique où les enfants peuvent explorer, jouer, apprendre dans notre station d\'apprentissage numérique supervisée pour inspirer la prochaine génération d\'esprits curieux et créer des souvenirs durables. Nos installations modernes accueillent les enfants de 6 mois à 14 ans, offrant un environnement sûr et passionnant pour un plaisir sans fin.',
      features: {
        safe: { title: 'Environnement Sûr', description: 'Toutes les aires de jeux sont conçues avec la sécurité comme priorité, avec rembourrage et zones supervisées.' },
        love: { title: 'Fait avec Amour', description: 'Chaque événement est conçu avec soin pour que votre enfant vive la meilleure expérience possible.' },
        quality: { title: 'Service de Qualité', description: 'Notre personnel formé assure un service de qualité et une attention aux détails pour chaque célébration.' },
        flexible: { title: 'Horaires Flexibles', description: 'Ouvert 7 jours sur 7 avec des options de réservation flexibles selon votre emploi du temps.' },
      },
      location: 'Adresse',
      callUs: 'Appelez-nous',
      happyKids: 'Enfants Heureux',
    },
    // Footer
    footer: {
      slogan: 'Jouer. Apprendre. Célébrer. Répéter. 🐻🍭 Aire de jeux intérieure et extérieure pour enfants de 6 mois à 14 ans.',
      quickLinks: 'Liens Rapides',
      ourServices: 'Nos Services',
      contactUs: 'Contactez-nous',
      birthdayPackage: 'Forfait Anniversaire',
      mascotReservation: 'Réservation Mascotte',
      candyBar: 'Bar à Bonbons',
      stemEducation: 'Éducation STEM',
      copyright: '© 2024 Mascot&Candy. Tous droits réservés.',
      privacyPolicy: 'Politique de Confidentialité',
      termsOfService: 'Conditions d\'Utilisation',
    },
    // Birthday Booking Page
    birthdayBooking: {
      backToHome: 'Retour à l\'accueil',
      title: 'Réserver une Fête d\'Anniversaire',
      subtitle: 'Créez des souvenirs magiques pour votre enfant avec nos forfaits anniversaire',
      choosePackage: 'Choisissez Votre Forfait',
      yourDetails: 'Vos Coordonnées',
      fullName: 'Nom Complet',
      fullNamePlaceholder: 'Entrez votre nom complet',
      mobilePhone: 'Téléphone Mobile',
      partyDate: 'Date de la Fête',
      selectDate: 'Sélectionnez une date',
      numberOfGuests: 'Nombre d\'Invités',
      bookBirthdayParty: 'Réserver la Fête',
      booking: 'Réservation...',
      successTitle: 'Fête d\'anniversaire réservée avec succès!',
      successDescription: 'Nous vous contacterons bientôt pour confirmer votre réservation.',
      errorTitle: 'Échec de la réservation',
      errorDescription: 'Veuillez réessayer plus tard',
      selectDateAndPackage: 'Veuillez sélectionner une date et un forfait',
    },
    // Mascot Booking Page
    mascotBooking: {
      backToHome: 'Retour à l\'accueil',
      title: 'Réserver une Mascotte',
      subtitle: 'Donnez vie au personnage préféré de votre enfant lors de votre événement',
      chooseMascot: 'Choisissez Votre Mascotte',
      yourDetails: 'Vos Coordonnées',
      fullName: 'Nom Complet',
      fullNamePlaceholder: 'Entrez votre nom complet',
      mobilePhone: 'Téléphone Mobile',
      eventDate: 'Date de l\'Événement',
      selectDate: 'Sélectionnez une date',
      eventDetails: 'Détails de l\'Événement (Optionnel)',
      eventDetailsPlaceholder: 'Parlez-nous de votre événement (lieu, heure, demandes spéciales...)',
      reserveMascot: 'Réserver la Mascotte',
      booking: 'Réservation...',
      successTitle: 'Réservation de mascotte réussie!',
      successDescription: 'Nous vous contacterons bientôt pour confirmer votre réservation.',
      errorTitle: 'Échec de la réservation',
      errorDescription: 'Veuillez réessayer plus tard',
      selectDateAndMascot: 'Veuillez sélectionner une date et une mascotte',
    },
    // Candy Bar Booking Page
    candyBarBooking: {
      backToHome: 'Retour à l\'accueil',
      title: 'Réserver un Bar à Bonbons',
      subtitle: 'Ajoutez de la douceur à votre événement avec notre service de bar à bonbons',
      chooseSelection: 'Choisissez Votre Sélection',
      yourDetails: 'Vos Coordonnées',
      fullName: 'Nom Complet',
      fullNamePlaceholder: 'Entrez votre nom complet',
      mobilePhone: 'Téléphone Mobile',
      eventDate: 'Date de l\'Événement',
      selectDate: 'Sélectionnez une date',
      numberOfGuests: 'Nombre d\'Invités',
      bookCandyBar: 'Réserver le Bar à Bonbons',
      booking: 'Réservation...',
      successTitle: 'Réservation du bar à bonbons réussie!',
      successDescription: 'Nous vous contacterons bientôt pour confirmer votre réservation.',
      errorTitle: 'Échec de la réservation',
      errorDescription: 'Veuillez réessayer plus tard',
      selectDateAndSelection: 'Veuillez sélectionner une date et une sélection de bonbons',
    },
    // STEM Registration Page
    stemRegistration: {
      backToHome: 'Retour à l\'accueil',
      title: 'Programme Éducation STEM',
      subtitle: 'Inscrivez votre enfant à notre passionnant programme STEM où Sciences, Technologie, Ingénierie et Mathématiques se rejoignent!',
      freeBadge: 'GRATUIT avec l\'entrée • Âges 4-14 ans',
      whatLearn: 'Ce Que Votre Enfant Apprendra',
      subjects: {
        science: { name: 'Sciences', description: 'Explorer le monde par des expériences' },
        technology: { name: 'Technologie', description: 'Apprendre le codage et les compétences numériques' },
        engineering: { name: 'Ingénierie', description: 'Construire et créer des projets incroyables' },
        maths: { name: 'Mathématiques', description: 'Résolution de problèmes et pensée logique' },
      },
      selectAgeGroup: 'Sélectionnez le Groupe d\'Âge',
      ageGroups: {
        '4-6': { label: '4-6 ans', description: 'Petits Explorateurs' },
        '7-10': { label: '7-10 ans', description: 'Jeunes Scientifiques' },
        '11-14': { label: '11-14 ans', description: 'Jeunes Innovateurs' },
      },
      whyChoose: 'Pourquoi Choisir Notre Programme STEM?',
      benefits: [
        'Expériences d\'apprentissage pratique',
        'Instructeurs qualifiés et passionnés',
        'Petits groupes pour une attention personnalisée',
        'Programme amusant et engageant',
      ],
      registerChild: 'Inscrivez Votre Enfant',
      parentName: 'Nom du Parent/Tuteur',
      parentNamePlaceholder: 'Entrez votre nom complet',
      childName: 'Nom de l\'Enfant',
      childNamePlaceholder: 'Entrez le nom de l\'enfant',
      mobilePhone: 'Téléphone Mobile',
      emailAddress: 'Adresse Email',
      emailPlaceholder: 'votre@email.com',
      additionalInfo: 'Informations Supplémentaires (Optionnel)',
      additionalInfoPlaceholder: 'Besoins spéciaux ou questions...',
      registerForStem: 'S\'inscrire au STEM',
      registering: 'Inscription...',
      successTitle: 'Inscription STEM réussie!',
      successDescription: 'Nous vous contacterons bientôt avec plus de détails sur notre programme STEM.',
      errorTitle: 'Échec de l\'inscription',
      errorDescription: 'Veuillez réessayer plus tard',
      selectAgeGroupError: 'Veuillez sélectionner un groupe d\'âge',
    },
    // Common
    common: {
      hoursOfPlayTime: 'heures de jeu',
      hoursOfPlay: 'heures de jeu',
      snacks: 'collations',
      fullPartyExperience: 'Expérience fête complète',
    },
  },
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('language') || 'en';
    }
    return 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'fr' : 'en');
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t, translations: translations[language] }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
